/**
 * The Work-home composer's analytics vocabulary: the `entry_point` tokens, the
 * `surface` union they are reported against, and the payload shape a composer
 * CTA/view carries.
 *
 * WHAT IS HERE AND WHAT IS NOT. The tokens and the payload types are contract —
 * they name events a warehouse schema already keys on, so two spellings of
 * `composer_mode_toggle` is two events. The EMIT is not: `trackWorkEvent` reaches
 * the analytics pipeline, and a rendered surface in this package never emits.
 * `TrackWorkHomeComposerCta` is the seam — the app supplies the emit, the
 * component supplies the payload, and the payload is what the suites pin.
 */

/** The Work analytics surfaces a Work event may be reported against. */
export type WorkSurface =
  | 'work_home'
  | 'project_chat'
  | 'in_session'
  | 'workflow'
  | 'voice'
  | 'settings'
  | 'sidebar';

export type AddMenuPickType =
  | 'session'
  | 'patient'
  | 'file'
  | 'sources'
  | 'routine'
  | 'recent_sessions';

export type AddMenuSubmenuType =
  | 'recent_sessions'
  | 'patient'
  | 'sources'
  | 'routine'
  | 'speed';

/** Snowplow `entry_point` tokens for Work home composer interactions. */
export const WORK_HOME_COMPOSER_ENTRY = {
  dictation: 'composer_dictation',
  modeToggle: 'composer_mode_toggle',
  approvalMode: (mode: 'manual' | 'auto') => `composer_approval_${mode}`,
  addMenuOpen: 'composer_add_menu',
  addMenuPick: (item: AddMenuPickType) => `composer_add_menu_${item}`,
  addMenuSubmenuOpen: (item: AddMenuSubmenuType) => `composer_add_menu_submenu_${item}`,
  speed: (mode: 'fast' | 'normal') => `composer_speed_${mode}`,
  slashPanelOpen: 'composer_slash_panel',
  slashPanelPick: (kind: string) => `composer_slash_${kind}`,
  mentionPanelOpen: 'composer_mention_panel',
  mentionPanelPick: (type: string) => `composer_mention_${type}`,
} as const;

/** The props a Work-home composer view/CTA event carries beyond its entry point. */
export interface WorkHomeComposerAnalyticsProps {
  surface?: WorkSurface;
  work_mode?: string;
}

/** Ranking tier that produced a Suggested-work row. */
export type WorkHomeSuggestionTier = 'contextual' | 'derived' | 'discovery';

/** Home focus at Suggested-work render time. */
export type WorkHomeFocusKind = 'none' | 'visit' | 'patient';

/**
 * Which ordering policy produced the shelf. `ranked` is the tiered heuristic;
 * `shuffled` is the experiment arm that randomises the idle composer so slot
 * effects can be separated from content effects.
 */
export type WorkHomeRankingPolicy = 'ranked' | 'shuffled';

/** Whole calendar days, bucketed, so an age never reconstructs a date. */
export type WorkHomeAgeBucket = '0' | '1-2' | '3-6' | '7-13' | '14-29' | '30+';

/**
 * Non-PHI ranking features for one Suggested-work row on one render. Every
 * field is a closed enum, a bounded integer, or a pseudonymous id — nothing
 * here can carry a title, a name, or prompt text. All optional so the existing
 * shown/picked callers stay valid.
 */
export interface WorkHomeSuggestionAnalyticsFeatures {
  render_id?: string;
  slot_index?: number;
  slot_count?: number;
  suggestion_tier?: WorkHomeSuggestionTier;
  ranking_policy?: WorkHomeRankingPolicy;
  focus_kind?: WorkHomeFocusKind;
  /** Pseudonymous clinical task id on leftover rows — the warehouse join key, like project_id. */
  task_id?: string;
  leftover_count?: number;
  open_task_count?: number;
  visit_age_bucket?: WorkHomeAgeBucket;
}

/** Shown/picked payload: composer props plus optional ranking features. */
export type WorkHomeSuggestionAnalyticsProps = WorkHomeComposerAnalyticsProps &
  WorkHomeSuggestionAnalyticsFeatures;

/**
 * How a routine run reached the composer's analytics — the composer `+` menu or
 * the Work-home play button. Vocabulary rather than mechanism: the two spellings
 * are keyed by a warehouse schema, so they belong beside the entry-point tokens.
 */
export type WorkHomeComposerRoutineTriggerSource =
  | 'work_composer_add_menu'
  | 'work_home_play';

/** The props a Work-home composer routine-run event carries. */
export interface WorkHomeComposerRoutineActionedProps {
  surface?: WorkSurface;
  work_mode?: string;
  workflow_id?: string;
  trigger_source: WorkHomeComposerRoutineTriggerSource;
}

/**
 * The `add_menu_<item>` token a linked context item maps to.
 *
 * A PURE MAPPER, and here rather than beside the emitters because it is the
 * vocabulary's own projection: `library_document` reports as `sources`, which is
 * a naming decision the warehouse schema depends on and NOT something the emit
 * mechanism knows. It lived next to `trackWorkEvent` only because that is where
 * it was first written; the composer chrome in this package needs it to build
 * the token it hands to the emit seam, and a second copy of this mapping would
 * silently split one event into two.
 */
export const linkedContextItemAddMenuPickType = (
  type: 'session' | 'patient' | 'library_document'
): AddMenuPickType => {
  if (type === 'session') return 'session';
  if (type === 'library_document') return 'sources';
  return 'patient';
};

/**
 * Bounded `entry_point` kinds for Suggested work. Fail-closed: anything not
 * in this set is `custom_routine` (bare ObjectId) or `other`. Do not send a
 * raw row id — leftover tasks and session suffixes are unbounded. A leftover
 * clinical task also carries an ObjectId, so callers that know the row is a
 * task pass `leftoverTask` or it lands in the routine bucket.
 */
const WORK_HOME_SUGGESTION_ANALYTICS_KINDS = new Set<string>([
  'leftover_task',
  'summarise-recent-visits',
  'file-document',
  'clinic-job-list',
  'promised-follow-ups',
  'find-patients-with-condition',
  'draft-referral-letter',
  'draft-from-pasted-notes',
  'ask-clinical-question',
  'create-routine',
  'catch-up-on-patient',
  'composer-draft-referral-letter',
  'look-up-treatment-plan',
  'library-capabilities',
  'import-guidelines',
  'sessions-capabilities',
  'continue-session',
  'pre-chart-patient',
  'link-patients-to-sessions',
  'get-ehr-patients-this-week',
  'connect-ehr',
  'cold-start-connect-calendar-ehr',
  'cold-start-paste-referral-reply',
  'cold-start-heidi-capabilities',
  'import-patients',
  'tray-pre-chart',
  'tray-push-notes',
  'tray-look-up-treatment',
  'tray-draft-follow-up-sms',
  'tray-summarise-admin-tasks',
  'tray-find-documents',
  'tray-prepare-consult',
  'tray-create-referral',
  'tray-check-action-today',
  'custom_routine',
  'other',
]);

const OBJECT_ID_KIND = /^[a-f0-9]{24}$/i;

export const workHomeSuggestionAnalyticsKind = (
  suggestionId: string,
  options?: { leftoverTask?: boolean }
): string => {
  if (options?.leftoverTask) return 'leftover_task';
  const separator = suggestionId.indexOf(':');
  const kind = separator === -1 ? suggestionId : suggestionId.slice(0, separator);
  if (WORK_HOME_SUGGESTION_ANALYTICS_KINDS.has(kind)) return kind;
  return OBJECT_ID_KIND.test(kind) ? 'custom_routine' : 'other';
};

/**
 * The emit seam for a Work-home composer CTA.
 *
 * A component in this package builds the payload and calls this; the app-side
 * host binds it to the tracker. That split is what keeps the analytics
 * mechanism app-resolved while the payload stays testable where the component
 * that decides it lives.
 */
export type TrackWorkHomeComposerCta = (
  entryPoint: string,
  props?: WorkHomeComposerAnalyticsProps
) => void;

/**
 * The emit seam for a Work-home composer panel/menu OPENING.
 *
 * Same split as the CTA seam above and a separate type rather than a reuse of
 * it, because the two are different events (`viewed` vs `cta_clicked`) even
 * though the arguments coincide: a host that binds one to the other is a bug a
 * shared alias would hide.
 */
export type TrackWorkHomeComposerViewed = (
  entryPoint: string,
  props?: WorkHomeComposerAnalyticsProps
) => void;

/**
 * The emit seam for a Work-home suggestion row becoming visible.
 *
 * Same split as the CTA seam: the package passes a bounded analytics kind
 * (via `workHomeSuggestionAnalyticsKind`), the app emits. The warehouse
 * action is `viewed` with a `suggestion_*` entry_point, not a new enum
 * value. This type stays separate from the composer-viewed seam so a host
 * cannot bind the two by accident.
 */
export type TrackWorkHomeSuggestionShown = (
  suggestionId: string,
  props?: WorkHomeSuggestionAnalyticsProps
) => void;

/**
 * The emit seam for a Work-home suggestion row being PICKED. Carries the same
 * `render_id` + `slot_index` its shown event did, which is the join key.
 */
export type TrackWorkHomeSuggestionPicked = (
  suggestionId: string,
  props?: WorkHomeSuggestionAnalyticsProps
) => void;

/**
 * The emit seam for a Work-home activity panel CLOSING.
 *
 * Same arguments as the viewed seam and a separate type for the same reason:
 * the events differ (`dismissed` vs `viewed`), so a host that binds one to
 * the other is a bug a shared alias would hide.
 */
export type TrackWorkHomeComposerDismissed = (
  entryPoint: string,
  props?: WorkHomeComposerAnalyticsProps
) => void;

/**
 * The emit seam for a routine RUN started from the composer.
 *
 * Takes one props object with no entry point, matching the event it reports: a
 * routine run is keyed by `trigger_source` and `workflow_id`, not by a composer
 * token.
 */
export type TrackWorkHomeComposerRoutineActioned = (
  props: WorkHomeComposerRoutineActionedProps
) => void;
